<script setup>
import { computed, ref, watch, provide } from 'vue';
import ContactPanel from 'dashboard/routes/dashboard/conversation/ContactPanel.vue';
import CustomInfoPanel from 'dashboard/routes/dashboard/conversation/CustomInfoPanel.vue';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { useWindowSize } from '@vueuse/core';
import { vOnClickOutside } from '@vueuse/components';
import wootConstants from 'dashboard/constants/globals';

const props = defineProps({
  currentChat: {
    required: true,
    type: Object,
  },
});

const { uiSettings, updateUISettings } = useUISettings();
const { width: windowWidth } = useWindowSize();

// Cache global para manter os CustomInfoPanels em memória
const customInfoPanelCache = ref(new Map());

// Adiciona o painel atual ao cache se não existir
watch(
  () => props.currentChat.id,
  newId => {
    if (newId && !customInfoPanelCache.value.has(newId)) {
      customInfoPanelCache.value.set(newId, {
        id: newId,
        inboxId: props.currentChat.inbox_id,
      });
    }
  },
  { immediate: true }
);

const activeTab = computed(() => {
  const {
    is_contact_sidebar_open: isContactSidebarOpen,
    is_custom_info_panel_open: isCustomInfoPanelOpen,
  } = uiSettings.value;

  if (isContactSidebarOpen) {
    return 0;
  }
  if (isCustomInfoPanelOpen) {
    return 1;
  }
  return null;
});

const isSmallScreen = computed(
  () => windowWidth.value < wootConstants.SMALL_SCREEN_BREAKPOINT
);

const closeContactPanel = () => {
  if (isSmallScreen.value && uiSettings.value?.is_contact_sidebar_open) {
    updateUISettings({
      is_contact_sidebar_open: false,
      is_copilot_panel_open: false,
    });
  }
};

const closeCustomInfoPanel = () => {
  updateUISettings({
    is_custom_info_panel_open: false,
  });
};

// Provide para que o CustomInfoPanel possa acessar o cache
provide('customInfoPanelCache', customInfoPanelCache);
</script>

<template>
  <div
    v-on-click-outside="[
      () => closeContactPanel(),
      {
        ignore: [
          'dialog.ProseMirror-prompt-backdrop',
          '[data-popover-content]',
          '[data-popover-backdrop]',
        ],
      },
    ]"
    class="bg-n-surface-2 h-full overflow-hidden flex flex-col fixed top-0 z-40 transition-transform duration-300 ease-in-out ltr:right-0 rtl:left-0 md:static ltr:border-l rtl:border-r border-n-weak shadow-lg md:shadow-none"
    :class="[
      {
        'md:flex': activeTab === 0 || activeTab === 1,
        'md:hidden': activeTab === null,
        'w-full max-w-sm md:w-[320px] md:min-w-[320px] 2xl:min-w-[360px] 2xl:w-[360px]':
          activeTab === 0,
        'w-full md:w-[700px] md:min-w-[700px]': activeTab === 1,
      },
    ]"
  >
    <div class="flex flex-1 overflow-auto">
      <div v-show="activeTab === 0" class="w-full h-full">
        <keep-alive>
          <ContactPanel
            key="contact-panel"
            :conversation-id="currentChat.id"
            :inbox-id="currentChat.inbox_id"
          />
        </keep-alive>
      </div>
      <div v-show="activeTab === 1" class="w-full h-full relative">
        <!-- Renderiza todos os CustomInfoPanels em cache, mas mostra apenas o atual -->
        <keep-alive>
          <CustomInfoPanel
            v-for="panel in customInfoPanelCache.values()"
            v-show="panel.id === currentChat.id"
            :key="`custom-info-panel-${panel.id}`"
            :conversation-id="panel.id"
            :inbox-id="panel.inboxId"
            @close="closeCustomInfoPanel"
          />
        </keep-alive>
      </div>
    </div>
  </div>
</template>
